import { useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useMe } from "../../lib/me";
import { cx, ErrorText } from "../../ui";
import { DUE_GROUP_LABEL, filterTasks, groupByDue, STATUSES, VIEWS, type StatusTab, type Task, type TaskView } from "./model";
import { QuickAdd } from "./QuickAdd";
import { TaskRow } from "./TaskRow";
import { memberLabel, useMembers, useTaskActions } from "./use-task-actions";
import { useNow, useTasks } from "./use-tasks";

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return !["checkbox", "radio", "button", "submit"].includes(target.type);
  return target.isContentEditable || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}

const STATUS_LIST_LABEL: Record<StatusTab, string> = { open: "Open tasks", done: "Done tasks", cancelled: "Cancelled tasks" };

/**
 * Tasks (WP-053): one header line with search, a quiet quick add, view and status chips, and the
 * list without a card around it. Open tasks are grouped by when they are due; a row opens the
 * task's own page.
 */
export function TasksPage({ workspaceId }: { workspaceId: string }) {
  const me = useMe();
  const now = useNow();
  const navigate = useNavigate();
  const ws = me.workspaces.find((w) => w.id === workspaceId);
  const personal = ws?.name === "Personal";

  const [view, setView] = useState<TaskView>("mine");
  const [status, setStatus] = useState<StatusTab>("open");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(0);
  const rows = useRef<Array<HTMLLIElement | null>>([]);
  const quickAdd = useRef<HTMLInputElement>(null);
  const searchId = useId();

  const state = useTasks(workspaceId, status);
  const members = useMembers(workspaceId);
  const actions = useTaskActions();
  const visible = useMemo(
    () => (state.status === "ready" ? filterTasks(state.tasks, { view, status, search }, me.uid) : []),
    [state, view, status, search, me.uid],
  );
  // Open tasks show in due-date groups, so keyboard order follows the groups, not the raw list.
  const groups = useMemo(() => (status === "open" ? groupByDue(visible, now) : []), [status, visible, now]);
  const ordered = useMemo(() => (status === "open" ? groups.flatMap((g) => g.tasks) : visible), [status, groups, visible]);
  const current = ordered[Math.min(active, ordered.length - 1)];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      const move = (to: number) => {
        const next = Math.max(0, Math.min(ordered.length - 1, to));
        setActive(next);
        rows.current[next]?.focus();
      };
      if (e.key === "n") quickAdd.current?.focus();
      else if (e.key === "j") move(active + 1);
      else if (e.key === "k") move(active - 1);
      else if (e.key === "x" && current !== undefined && current.status !== "cancelled")
        actions.toggle(workspaceId, current.id, current.status !== "done");
      else if ((e.key === "e" || e.key === "Enter") && current !== undefined)
        void navigate({ to: "/w/$workspaceId/tasks/$taskId", params: { workspaceId, taskId: current.id } });
      else return;
      e.preventDefault();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [ordered.length, active, current, actions, workspaceId, navigate]);

  const row = (t: Task) => {
    const at = ordered.indexOf(t);
    return (
      <TaskRow
        key={t.id}
        workspaceId={workspaceId}
        task={t}
        now={now}
        active={current?.id === t.id}
        assigneeLabel={t.assigneeUid === null ? null : memberLabel(members.data, t.assigneeUid)}
        showVisibility={!personal}
        rowRef={(el) => {
          rows.current[at] = el;
        }}
        onFocus={() => setActive(at)}
        onToggle={() => actions.toggle(workspaceId, t.id, t.status !== "done")}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="sticky top-12 z-10 flex h-11 items-center gap-3 border-b border-border bg-bg md:top-0">
        <h1 className="text-base font-semibold text-text">Tasks</h1>
        <label htmlFor={searchId} className="sr-only">
          Search tasks
        </label>
        <input
          id={searchId}
          type="search"
          placeholder="Search titles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ml-auto h-8 w-full min-w-0 max-w-xs rounded-md border-0 bg-surface-2 px-3 text-sm text-text placeholder:text-muted"
        />
      </div>

      <QuickAdd workspaceId={workspaceId} personal={personal} now={now} inputRef={quickAdd} />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div role="group" aria-label="Show" className="flex flex-wrap gap-0.5">
          {VIEWS.map((v) => (
            <Chip key={v.key} pressed={view === v.key} onClick={() => setView(v.key)}>
              {v.label}
            </Chip>
          ))}
        </div>
        <div role="group" aria-label="Status" className="ml-auto flex gap-0.5">
          {STATUSES.map((s) => (
            <Chip key={s.key} pressed={status === s.key} onClick={() => setStatus(s.key)}>
              {s.label}
            </Chip>
          ))}
        </div>
      </div>

      <section aria-label="Task list" className="-mx-2 flex flex-col gap-4">
        {state.status === "loading" ? <p className="px-2 text-sm text-muted">Loading tasks…</p> : null}
        {state.status === "error" ? (
          <div className="px-2">
            <ErrorText>Tasks could not be loaded: {state.message}</ErrorText>
          </div>
        ) : null}
        {state.status === "ready" && visible.length === 0 ? (
          <EmptyState filtered={search.trim().length > 0 || state.tasks.length > 0} status={status} />
        ) : null}
        {visible.length > 0 && status === "open" ? (
          <div role="list" aria-label={STATUS_LIST_LABEL.open} className="flex flex-col gap-4">
            {groups.map((g) => (
              <div key={g.group} role="listitem" className="flex flex-col">
                <h2 className="px-2 pb-1 text-xs font-semibold text-muted">
                  {DUE_GROUP_LABEL[g.group]} <span className="font-normal">{g.tasks.length}</span>
                </h2>
                <ul aria-label={DUE_GROUP_LABEL[g.group]} className="flex flex-col">
                  {g.tasks.map((t) => row(t))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}
        {visible.length > 0 && status !== "open" ? (
          <ul aria-label={STATUS_LIST_LABEL[status]} className="flex flex-col">
            {ordered.map((t) => row(t))}
          </ul>
        ) : null}
      </section>
      <p className="hidden text-xs text-muted sm:block">
        Keys: <kbd>n</kbd> new · <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>x</kbd> done · <kbd>e</kbd> open
      </p>
    </div>
  );
}

function Chip({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cx("min-h-7 rounded-full px-2.5 text-xs", pressed ? "bg-surface-2 font-semibold text-text" : "text-muted hover:bg-surface-2 hover:text-text")}
    >
      {children}
    </button>
  );
}

function EmptyState({ filtered, status }: { filtered: boolean; status: StatusTab }) {
  const text = filtered
    ? "No tasks match these filters."
    : status === "done"
      ? "Nothing done yet."
      : status === "cancelled"
        ? "Nothing cancelled."
        : "No open tasks. Add one above, or with /task in chat.";
  return <p className="px-2 py-6 text-center text-sm text-muted">{text}</p>;
}
